<script lang="ts">
	import type { SbLink } from '$lib/storyblok/types';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import Button from '$lib/buttons/v3/button.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { event } from '$lib/utils/tracking';
	import { page } from '$app/state';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';
	import { innerWidth } from 'svelte/reactivity/window';
	import { checkVisibility } from '$lib/actions/checkVisibility';

	interface Props {
		content: ISbStoryData<{
			title: string;
			desktop_title: ISbRichtext;
			subtitle: string;
			hover_color: string;
			image: ISbStoryData[];
			target: SbLink;
		}>['content'];
		behavior: 'interactive' | 'static';
	}

	let { content, behavior = 'interactive' }: Props = $props();

	let subtitleHeight = $state(0);
	let linkUrl = $derived(getSbUrl(content.target));
	let isMobile = $derived(!innerWidth.current || innerWidth.current < 1024);
	let showButton = $state(false);
</script>

<a
	href={linkUrl}
	use:editable={content}
	class="group relative block h-full w-full overflow-hidden rounded-xl lg:rounded-2xl"
	style="background-color: {content.hover_color || 'transparent'};"
	onclick={() => {
		event('Accordion Gallery Card Click', {
			path: page.url.pathname,
			text: content.title,
			href: linkUrl
		});
	}}
>
	{#if content.image.length > 0}
		<div
			class={[
				'relative aspect-[1.5] h-full w-full transition-opacity duration-[460ms]',
				behavior === 'static' ? 'lg:aspect-3/2' : 'lg:group-hover:opacity-0'
			]}
		>
			<Blok content={content.image[0]} />
			<div class="text-gradient absolute inset-x-0 bottom-0 h-20"></div>
		</div>
	{/if}

	{#if behavior === 'static' || isMobile}
		<article class="text-pretty p-5 text-white" style="background-color: {content.hover_color};">
			<h1
				class="font-rhymes text-[32px]/[110%] tracking-[-.64px] lg:text-[36px]/[90%] lg:tracking-[-.72px]"
			>
				{content.title}
			</h1>

			<div
				class="mt-3 flex items-center justify-between gap-x-8"
				use:checkVisibility={{ threshold: 0 }}
				onvisible={({ detail }) => {
					if (!detail) return;

					showButton = true;
				}}
			>
				<h2 bind:clientHeight={subtitleHeight} class="text-[16px]/[150%] tracking-[-.16px]">
					{content.subtitle}
				</h2>

				<Button
					class={[
						'btn-blur transition-all',
						showButton
							? '-translate-x-0 opacity-100 delay-100 duration-[460ms]'
							: '-translate-x-1 opacity-0'
					]}
					title="Go to page"
				>
					<ArrowRight />
				</Button>
			</div>
		</article>
	{/if}

	{#if behavior === 'interactive' && !isMobile}
		<div
			class="absolute bottom-9 right-8 -translate-x-1 opacity-0 transition-all duration-[460ms] group-hover:translate-x-0 group-hover:opacity-100"
		>
			<Button class="btn-white btn-small" title="Go to page">
				<ArrowRight />
			</Button>
		</div>
		<div
			class="text-translate absolute inset-x-0 bottom-0 flex items-center justify-between p-5 text-white transition-transform duration-[460ms] lg:items-end lg:p-6"
			style="--translate-text-y: {subtitleHeight + 24}px;"
		>
			<article>
				<div
					class="font-rhymes text-[32px]/[110%] font-light tracking-[-.64px] lg:text-[56px]/[90%] lg:tracking-[-1.68px]"
				>
					<h1 class="lg:hidden">
						{content.title}
					</h1>
					<div class="max-lg:hidden">
						<SbRichtext text={content.desktop_title || content.title} />
					</div>
				</div>

				<h2
					bind:clientHeight={subtitleHeight}
					class="mt-6 max-w-[80%] translate-y-2 text-[16px]/[150%] tracking-[-.16px] opacity-0 transition-all duration-[460ms] group-hover:translate-y-0 group-hover:opacity-100 lg:text-[20px]/[140%] lg:tracking-[-.20px]"
				>
					{content.subtitle}
				</h2>
			</article>
		</div>
	{/if}
</a>

<style>
	.group:hover {
		& .text-translate {
			transform: translate3d(0, 0, 0);
		}
	}

	.text-translate {
		@media (min-width: 1024px) {
			transform: translate3d(0, var(--translate-text-y), 0);
		}
	}

	.text-gradient {
		background: linear-gradient(0deg, rgba(18, 18, 18, 0.5) 0%, rgba(18, 18, 18, 0) 100%);
	}
</style>
